import axios, {AxiosPromise, AxiosResponse} from "axios";
import * as merge from 'deepmerge';
import {get, has, uniqBy} from "lodash";
import PQueue from "p-queue";
import {Entry, ApiResponse, List} from "./contract/notion";
import {BlockRequest} from "./contract/notion";
import {Options, Settings, SourceDef} from "./contract/source";
import {TransformEntryDef} from "./transformer/notion/entry";
import {TransformContentDef} from "./transformer/notion/content";
import {getKeyValue, passthrough} from './util/helper';
import {Nodes, queue} from './util/queue';
import {ContentApiDef} from ".";

const TTL = 30; /* minutes */

interface Routes {
  [key: string]: Entry;
}

interface Clients {
  content: BlockRequest;
  [key: string]: SourceDef;
}

type AddModelDef = (
  model: string,
  client: any,
  source: SourceDef,
  transformer: (self: ContentApiDef, model: string) => TransformEntryDef|TransformContentDef,
  adapter: (self: ContentApiDef, model: string) => any,
  options: Options,
) => void;

// create a content API Wrapper instance
class ContentApi {
  // internals
  protected _models: string[] = [];
  protected _nodes: Nodes = {};
  protected _options: Options = {};
  protected _routes: Routes = {};
  protected _client: Clients = {content: null};
  protected _adapter: {[key: string]: (self: ContentApiDef, model?: string) => any} = {};
  protected _transformer: {[key: string]: (self: ContentApiDef, model?: string) => any} = {};
  protected _source: {[key: string]: SourceDef} = {};
  protected _queue: PQueue;

  // Dynamically added method; refer to getEntries, getEntry or getEntryContent
  [key: string]: any;

  constructor(
    protected client: any, // eslint-disable-line @typescript-eslint/explicit-module-boundary-types
    protected source: SourceDef,
    protected transformer: (self: ContentApiDef, model: string) => TransformEntryDef|TransformContentDef,
    protected adapter: (self: ContentApiDef, model: string) => any,
    protected opt?: Options
  ) {
    // bootstrap
    this._adapter.content = adapter || passthrough;
    this._client.content = client;
    this._source.content = source;
    this._transformer.content = transformer ||  passthrough;
    this.options = {
      get: (key: string): any => get(this._options, key, {}),
      ...merge({
        content: {
          fetch: {cb: passthrough.apply(this, [this])},
          transformer: {cb: passthrough.apply(this, [this])},
          adapter: {cb: passthrough.apply(this, [this])},
        },
        queue: {
          concurrency: 3,
        },
        ttl: 60000, /* ms */
      }, opt || {})
    };

    this.queue = queue(this.nodes, this.options.get('queue'));

    // return with custom setter logic to update collections of models
    return new Proxy(this, {
      get(target: any, name: any, receiver: any): any {
        if (!Reflect.has(target, name)) return undefined;

        return Reflect.get(target, name, receiver);
      },
      set(target: any, name: any, value: any, receiver: any): any {
        const model = name.toString().substring(0, name.toString().length - 1);

        if (target._models.indexOf(model) > -1) {
          // shallow merge pages, new value wins
          return Reflect.set(target, name, uniqBy([...(value || []), ...(target[name] || [])], 'id'), receiver);
        }

        return Reflect.set(target, name, value, receiver);
      }
    });
  }

  cancel(id: string): void { // eslint-disable-line @typescript-eslint/no-unused-vars
    this.queue.clear();
  }

  // define a model to fetch and process entries
  addModel: AddModelDef = (
    model = 'page',
    client,
    source,
    transformer,
    adapter,
    options = {}
  ) => {
    // bootstrap
    this._models.push(model);
    this.options[model] = {
      ...merge({
        Schema: {
          title: 'Name',
          slug: ['slug', 'Slug'],
          redirect: ['redirect', 'Redirect Slug']
        },
        fetch: {cb: passthrough.apply(this, [this])},
        transformer: {cb: passthrough.apply(this, [this])},
        adapter: {cb: passthrough.apply(this, [this])},
      }, options)
    };

    // processors
    this._client[model as string] = client as SourceDef;
    this._source[model as string] = source as SourceDef;
    this._adapter[model] = adapter || passthrough;
    this._transformer[model] = transformer || passthrough;

    // methods
    const Model: string = model.charAt(0).toUpperCase() + model.slice(1);
    this[`get${Model}s`] = (options: Settings): List =>
      this._getEntries.apply(this,[{model, ...options}]);
    this[`get${Model}`] = (id: string, options: Settings): List =>
      this._getEntry.apply(this, [id, {model, ...options}]);
    this[`get${Model}Content`] = (id: string, options: Settings): Entry =>
      this._getContent.apply(this, [id, {model, ...options}]);

    // container
    this[`${model}s`] = null; // []
  }

  // ...model-specific methods will be added

  // get a list of entries
  _getEntries(options: Settings = {model: 'page'}): AxiosPromise<List> {
    // bootstrap
    const {model, ...opt} = options;
    this.options[model] = {
      ...this.options[model],
      ...opt
    };

    // processors
    const client = getKeyValue(model as any, this._client);
    const source = getKeyValue(model as any, this._source);
    const fetch = client.entries(source);
    const transform = this._transformer[model](this, model); // could not assign 'this' :(
    const adapt = this._adapter[model](this, model);

    // callbacks
    const {cb: afterFetch} = this.options.get(`${model}.fetch`);
    const {cb: afterTransform} = this.options.get(`${model}.transformer`);
    const {cb: afterAdapt} = this.options.get(`${model}.adapter`);

    return fetch(opt)
      .then((response: ApiResponse) => {
        if (!response || !response.data) throw new Error('Invalid response');
        const {data: results, meta} = response;

        this.options[model].meta = meta;

        // shallow merge pages, new value wins
        this[`${model}s`] = uniqBy([...(Array.isArray(results) ? results : []), ...(this[`${model}s`] || [])], 'id');
        return results;
      })
      .then(afterFetch)
      .then(transform)
      .then(afterTransform)
      .then(adapt)
      .then(afterAdapt)
      .then((results: Entry[]) => {
        this[`${model}s`] = uniqBy([...(Array.isArray(results) ? results : []), ...(this[`${model}s`] || [])], 'id');
        return results;
      });
  }

  // get a single entry
  _getEntry(id: string, options: Settings = {model: 'page'}): AxiosPromise<List> {
    // bootstrap
    const {model, ...opt} = options;
    this.options[model] = {
      ...this.options[model],
      ...opt
    };

    // processors
    const client = getKeyValue(model as string, this._client);
    const source = getKeyValue(model as string, this._source);
    const fetch = client.entry(source);
    const transform = this._transformer[model](this, model); // could not assign 'this' :(
    const adapt = this._adapter[model](this, model);

    // callbacks
    const {cb: afterFetch} = this.options.get(`${model}.fetch`);
    const {cb: afterTransform} = this.options.get(`${model}.transformer`);
    const {cb: afterAdapt} = this.options.get(`${model}.adapter`);

    return fetch(id, opt)
      .then((response: ApiResponse) => {
        if (!response || !response.data) throw new Error('Invalid response');
        const {data: results} = response;

        this[`${model}s`] = uniqBy([...(Array.isArray(results) ? results : []), ...(this[`${model}s`] || [])], 'id');
        return results;
      })
      .then(afterFetch)
      .then(transform)
      .then(afterTransform)
      .then(adapt)
      .then(afterAdapt)
      .then((results: Entry[]) => {
        this[`${model}s`] = uniqBy([...(Array.isArray(results) ? results : []), ...(this[`${model}s`] || [])], 'id');
        return results;
      });
  }

  // render an entry
  _getContent(id: string, options: Settings = {model: 'page'}): PromiseLike<Entry> {
    // bootstrap
    const {model, ...opt} = options;

    // page object
    const entries = getKeyValue(`${model}s`, this);
    const entry = (entries || []).find((entry: Entry): boolean => entry.slug === id || entry.id === id);
    if (!entry) {
      throw new Error(`${model} ${id} could not be found`);
    } // @todo this only applies to in-app page routes

    // check cache
    const isCurrent = (
      (has(entry, 'stale_time') && has(entry, 'rendered_time')) &&
      (new Date(entry.rendered_time).getTime() <= new Date(entry.stale_time).getTime())
    );
    if (isCurrent) return entry;

    // processors
    this.options = {content: opt};
    const transform = this._transformer.content(this); // could not assign 'this' :(
    const adapt = this._adapter.content(this);

    // callbacks
    const {cb: afterFetch} = this.options.get('content.fetch');
    const {cb: afterTransform} = this.options.get('content.transformer');
    const {cb: afterAdapt} = this.options.get('content.adapter');

    const fetch = entry.content?.length
      ? new Promise((resolve) => resolve(entry))
      : this._getNode(id, {})
          .then((data: List) => {
            if (!data || !Array.isArray(data)) throw new Error('Invalid responc');

            entry.nodes = data;

            return entry;
          })
          .then(afterFetch)
          .then(transform)
          .then(afterTransform)

    return fetch
      .then(adapt)
      .then(afterAdapt)
      .then((result: Entry) => {
        if (result) {
          const now = new Date();
          result.rendered_time = now.toISOString();
          result.stale_time = new Date(now.getTime() + TTL * 60000).toISOString(); // @todo make TTL configurable

          // update entries collection
          const index = this[`${model}s`].findIndex((entry: Entry): boolean => entry.id === id);
          this[`${model}s`] = (index > -1)
            ? [...this[`${model}s`].slice(0, index), result, ...this[`${model}s`].slice(index + 1)]
            : [...this[`${model}s`], result];
        }

        return result
      })
  }

  // get a single node
  _getNode(id: string, options?: Settings): PromiseLike<List> {
    // processors
    this.options = {content: options};

    const client = getKeyValue('content', this._client);
    const source = getKeyValue('content', this._source);
    const fetch = client(source) as any;

    // node object
    const block = has(this.nodes, id) && this.nodes[id];

    // check cache
    const isCurrent = block && (
      (has(block, 'stale_time') && has(block, 'rendered_time')) &&
      (new Date(block.rendered_time).getTime() <= new Date(block.stale_time).getTime())
    );

    if (isCurrent) {
      return axios.get('')
        .then((response): List => {
          response.data = {...block};

          return response.data;
        });
    }

    return fetch(id)
      .then((response: AxiosResponse<List>): List => {
        if (!response || !response.data) throw new Error('Invalid response');

        // update blocks collection
        const now = new Date();
        this.nodes[id] = {
          object: 'list',
          rendered_time: now.toISOString(),
          stale_time: new Date(now.getTime() + TTL * 60000).toISOString(), // @todo make TTL configurable
          results: response.data,
        };

        return response.data;
      });
  }

  get nodes(): Nodes {
    return this._nodes;
  }

  set nodes(value: Nodes) {
    this._nodes = value;
  }

  get options(): Options {
    return this._options;
  }

  set options(value: Options) {
    // deep merge options, new value wins
    this._options = merge((this._options || {}), value);
  }

  get queue(): PQueue {
    return this._queue;
  }

  set queue(value: PQueue) {
    this._queue = value;
  }

  get routes(): Routes {
    return this._routes;
  }

  set routes(value: Routes) {
    this._routes = value;
  }
}

export default ContentApi;
